import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import {
  getDecisionEngineAssignations,
  saveDecisionTree,
  updateDecisionEngine,
} from 'lib/api';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { Node } from 'pages/Settings/Components/Risk/Components/DecisionEngines/components/Create/types';
import {
  ENGINE_DESCRIPTIONS,
  EngineType,
  REVISION_OBJECTIVE_DESCRIPTIONS,
  RevisionObjective,
} from 'pages/Settings/Components/Risk/Components/DecisionEngines/components/Create/types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import MatrixEngine from './components/MatrixEngine';
import TreeEngine from './components/TreeEngine';

const DecisionTreeCreate = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [decisionNodesAssignation, setDecisionNodesAssignation] = useState<
    Node[]
  >([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [name, setName] = useState('');
  const [engineType, setEngineType] = useState<EngineType>(null);
  const [revisionObjective, setRevisionObjective] =
    useState<RevisionObjective>(null);

  const engineDescription = useMemo(() => {
    if (!revisionObjective) return 'Selecciona una revisión';
    return REVISION_OBJECTIVE_DESCRIPTIONS[revisionObjective];
  }, [revisionObjective]);

  const revisionDescription = useMemo(() => {
    if (!engineType) return 'Selecciona un motor';
    return ENGINE_DESCRIPTIONS[engineType];
  }, [engineType]);

  const fetchDecisionEngineNodes = async () => {
    try {
      if (!id) return;
      setLoading(true);
      const response = await getDecisionEngineAssignations(dispatch, id);
      setDecisionNodesAssignation(response.nodes);
      setEngineType(response.decisionEngine.engineType);
      setRevisionObjective(response.decisionEngine.revisionObjectiveId);
      setLoading(false);
    } catch (error) {
      errorHandler(dispatch, error, 'Error al obtener los nodos del motor');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);
      const body = {
        nodes: decisionNodesAssignation,
        id,
      };
      await updateDecisionEngine(dispatch, body);
      window.location.reload();
      showAlert(dispatch, 'success', 'Motor editado correctamente');
    } catch (error) {
      errorHandler(dispatch, error, 'Hubo un error al editar el motor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const body = {
        nodes: decisionNodesAssignation,
        name,
        engineType,
        revisionObjective,
      };
      await saveDecisionTree(dispatch, body);
      showAlert(dispatch, 'success', 'Motor creado correctamente');
      window.location.href = '/settings/risk/engines';
    } catch (error) {
      errorHandler(dispatch, error, 'Hubo un error al crear el motor');
    } finally {
      setLoading(false);
    }
  };

  const startButtons: CustomButtonProps[] = [
    {
      action: () => goBackToShow(),
      name: 'Volver',
      color: 'secondary',
    },
    {
      action: () => openConfirmationDialog(),
      name: 'Guardar motor',
      color: 'primary',
    },
  ];

  const goBackToShow = () => {
    window.location.href = '/settings/risk/engines';
  };

  const openConfirmationDialog = () => {
    if (engineType === 'tree') {
      if (!checkAllFatherHaveAnEmptyRange()) {
        alert(
          'Todo nodo padre debe tener un hijo con caso base. (Es la opción cuando no se cumplen las otras condiciones)',
        );
      } else if (!checkAllLeafsAreNegative()) {
        alert(
          'Debes cerrar las ramas con nodos de decisión (Aceptado, rechazado o advertencia)',
        );
      } else if (!checkAllFatherHave2Children()) {
        alert('Debes tener al menos dos hijos por cada nodo de decisión');
      } else {
        setOpenDialog(true);
      }
    } else if (engineType === 'matrix') {
      if (checkMinimumNodesForMatrix()) {
        setOpenDialog(true);
      } else {
        alert('Debes tener al menos un nodo de decisión');
      }
    }
  };

  const checkMinimumNodesForMatrix = () => {
    return decisionNodesAssignation.length >= 1;
  };

  const checkAllFatherHaveAnEmptyRange = () => {
    const allFatherIds = decisionNodesAssignation.map(
      (node) => node.fatherNodeId,
    );
    const allFatherIdsUnique = Array.from(new Set(allFatherIds));
    const oks: boolean[] = [];
    allFatherIdsUnique.forEach((fatherNodeId) => {
      if (fatherNodeId === 0) {
        return;
      }
      let isOk = false;
      const father = decisionNodesAssignation.find(
        (node) => node.id === fatherNodeId,
      );
      if (!father) {
        return (isOk = true);
      }
      father.ranges.forEach((range) => {
        if (range.value === '') isOk = true;
      });
      oks.push(isOk);
    });
    return oks.every((ok) => ok);
  };

  const checkAllLeafsAreNegative = () => {
    let isOk = true;
    const leafs = decisionNodesAssignation.filter(
      (node) => node.ranges.length === 0,
    );
    leafs.forEach((leaf) => {
      if (leaf.type !== 'final') {
        isOk = false;
      }
    });
    return isOk;
  };

  const checkAllFatherHave2Children = () => {
    let isOk = true;
    const nonFinalNdoes = decisionNodesAssignation.filter(
      (node) => node.type !== 'final',
    );
    nonFinalNdoes.forEach((node) => {
      if (node.ranges.length < 2) {
        isOk = false;
      }
    });

    return isOk;
  };

  useEffect(() => {
    void fetchDecisionEngineNodes();
  }, []);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        title={id ? 'Editar motor de decisiones' : 'Crear motor de decisiones'}
        type="pageHeader"
        buttons={startButtons}
      />
      <div
        style={{
          display: 'flex',
          padding: '10px 0px 20px',
          alignItems: 'center',
        }}
      >
        <div style={{ minWidth: 250 }}>
          <FormControl
            disabled={id ? true : false}
            fullWidth
            variant="outlined"
          >
            <InputLabel id="filter-label">
              Tipo de revisión de decisiones
            </InputLabel>
            <Select
              labelId="filter-label"
              id="Filtrar por estado"
              name="engineType"
              value={revisionObjective}
              onChange={(e: SelectChangeEvent<RevisionObjective>) => {
                setDecisionNodesAssignation([]);
                setRevisionObjective(e.target.value as RevisionObjective);
                setEngineType(null);
              }}
              label="Tipo de revisión de decisiones"
            >
              <MenuItem value="invoice">Facturas</MenuItem>
              <MenuItem value="creditLine">Línea de crédito</MenuItem>
              <MenuItem value="creditLine-payers">
                Línea de crédito - Deudores
              </MenuItem>
              <MenuItem value="factoring-request">Solicitud factoring</MenuItem>
              <MenuItem value="factoring-request-payers">
                Solicitud factoring - Deudores
              </MenuItem>
              <MenuItem value="credit-request">Solicitud de crédito</MenuItem>
              <MenuItem value="sgr-warranty-request">
                Solicitud de garantía SGR
              </MenuItem>
              <MenuItem value="confirming-request">
                Solicitud de confirming
              </MenuItem>
            </Select>
          </FormControl>
        </div>
        <Typography variant="body1" style={{ marginLeft: '20px' }}>
          {engineDescription}
        </Typography>
      </div>
      <div
        style={{
          display: 'flex',
          padding: '10px 0px 20px',
          alignItems: 'center',
        }}
      >
        <div style={{ minWidth: 250 }}>
          <FormControl
            disabled={id ? true : false}
            fullWidth
            variant="outlined"
          >
            <InputLabel id="filter-label">
              Tipo de motor de decisiones
            </InputLabel>
            <Select
              labelId="filter-label"
              id="Filtrar por estado"
              name="engineType"
              value={engineType}
              onChange={(e: SelectChangeEvent<EngineType>) => {
                setDecisionNodesAssignation([]);
                setEngineType(e.target.value as EngineType);
              }}
              label="Tipo de motor de decisiones"
            >
              <MenuItem value="tree">Árbol de decisiones</MenuItem>
              <MenuItem value="matrix">Matriz de decisiones</MenuItem>
            </Select>
          </FormControl>
        </div>
        <Typography variant="body1" style={{ marginLeft: '20px' }}>
          {revisionDescription}
        </Typography>
      </div>
      {!engineType || !revisionObjective ? (
        <Typography variant="body1" color="red">
          Selecciona un motor y un tipo de revisión para continuar
        </Typography>
      ) : null}
      <Divider />
      {revisionObjective && engineType === 'tree' && (
        <TreeEngine
          decisionNodesAssignation={decisionNodesAssignation}
          setDecisionNodesAssignation={setDecisionNodesAssignation}
          revisionObjective={revisionObjective}
        />
      )}
      {revisionObjective && engineType === 'matrix' && (
        <MatrixEngine
          decisionNodesAssignation={decisionNodesAssignation}
          setDecisionNodesAssignation={setDecisionNodesAssignation}
          revisionObjective={revisionObjective}
        />
      )}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {id ? 'Guardar Motor' : 'Crear motor de decisiones'}
        </DialogTitle>
        <DialogContent>
          {id ? (
            <DialogContentText>
              ¿Estás seguro que quieres guardar los cambios en este motor?
            </DialogContentText>
          ) : (
            <div>
              <DialogContentText>
                ¿Cómo le vas a llamar a este motor?
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                id="name"
                label="Nombre del árbol"
                type="text"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={() => setOpenDialog(false)} color="secondary">
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={() => (id ? handleUpdate() : handleSubmit())}
            color="primary"
          >
            {id ? 'Guardar' : 'Crear'}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DecisionTreeCreate;
